import React, { useEffect } from 'react'
import { BadgeVariant, Flex, Modal, StatusBadge, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { ProgressState, indexNodeStore } from 'mobxStores/IndexNodeStore'
import { observer } from 'mobx-react-lite'
import { getErrorCodesDescription } from 'utils/getErrorCodesDescription'
import SuccessApiKey from './SuccessApiKey'

const MainWrapper = styled(Flex)`
  width: 557px;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
  }
`

interface IndexNodeApiKeyModalProps {
  onDismiss?: () => void
  name: string
  licenseId: string
}

const HIDE_MODAL_TIMEOUT = 30000

const IndexNodeApiKeyModal: React.FC<IndexNodeApiKeyModalProps> = observer(({ onDismiss, name, licenseId }) => {
  const { t } = useTranslation()
  const { progress, error } = indexNodeStore

  const getModalContent = (progress: ProgressState) => {
    switch (progress) {
      case ProgressState.INITIAL:
        return (
          <Flex mt="24px" justifyContent="center" alignItems="center" fullWidth>
            <StatusBadge variant={BadgeVariant.PENDING} title={t('licenses.modal.continueInWallet')} />
          </Flex>
        )
      case ProgressState.PENDING:
        return (
          <Flex mt="24px" justifyContent="center" alignItems="center" fullWidth>
            <StatusBadge variant={BadgeVariant.PENDING} title={t('licenses.modal.transactionPending')} />
          </Flex>
        )
      case ProgressState.ERROR:
        return (
          <Flex mt="24px" justifyContent="center" alignItems="center" fullWidth>
            <StatusBadge variant={BadgeVariant.ERROR} title={getErrorCodesDescription(t, error)} />
          </Flex>
        )
      case ProgressState.SUCCESS:
        return <SuccessApiKey {...{ name, licenseId }} />
      default:
        return <></>
    }
  }

  const getModalTitle = () => {
    switch (progress) {
      case ProgressState.INITIAL:
      case ProgressState.PENDING:
      case ProgressState.CONFIRMING:
        return t('licenses.modal.signTransaction')
      case ProgressState.SUCCESS:
        return t('licenses.modal.showApiKey')
      case ProgressState.ERROR:
        return t('licenses.modal.error')
      default:
        return ''
    }
  }
  const hideCloseButton = () => {
    return [ProgressState.PENDING, ProgressState.CONFIRMING, ProgressState.INITIAL].includes(progress)
  }

  const resetState = () => {
    indexNodeStore.resetIndexNodeStore()
    onDismiss()
  }

  useEffect(() => {
    if (progress === ProgressState.SUCCESS) {
      setTimeout(() => {
        resetState()
      }, HIDE_MODAL_TIMEOUT)
    }
  }, [progress])

  return (
    <Modal
      titleFontVariant={TextVariant.BODY_BIG_BOLD}
      title={getModalTitle()}
      hideCloseButton={hideCloseButton()}
      onDismiss={() => resetState()}
    >
      <MainWrapper>{getModalContent(progress)}</MainWrapper>
    </Modal>
  )
})

export default IndexNodeApiKeyModal
